import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Radio, FileDown, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

const n = (v) => (v ?? 0).toLocaleString("it-IT");
const eur = (v) => `€ ${(v ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ChannelsOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [mailBusy, setMailBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get("/workspaces/overview-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `panoramica-canali-${data?.month}.pdf` });
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Generazione PDF non riuscita"); }
    setPdfBusy(false);
  };
  const sendPdf = async () => {
    setMailBusy(true);
    try { const r = await api.post("/workspaces/overview-pdf/send"); toast.success(`Panoramica inviata a ${r.data.to}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setMailBusy(false);
  };
  const load = () => { setLoading(true); api.get("/workspaces/overview").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);
  const monthLabel = data?.month ? new Date(data.month + "-01").toLocaleDateString("it-IT", { month: "long", year: "numeric" }) : "";
  const cols = [["Canale", "text-left"], ["Iscritti", ""], ["Campagne", ""], ["Inviati WA", ""], ["Consegnati", ""], ["SMS", ""], ["Clic", ""], ["Costo Meta+SMS", ""], ["Quota usata", "text-left w-44"]];

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="channels-overview">
      <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
        <div>
          <p className="font-semibold text-slate-900">Confronto canali · {monthLabel}</p>
          <p className="text-xs text-slate-500">Inviati, consegne, clic, costi e quota del mese per ogni canale</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-8 border-slate-200 text-xs" onClick={downloadPdf} disabled={!data || pdfBusy} data-testid="channels-overview-pdf">
            {pdfBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><FileDown className="h-3.5 w-3.5 mr-1" /> Scarica PDF</>}
          </Button>
          <Button size="sm" variant="outline" className="h-8 border-slate-200 text-xs" onClick={sendPdf} disabled={!data || mailBusy} data-testid="channels-overview-email">
            {mailBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Mail className="h-3.5 w-3.5 mr-1" /> Invia via email</>}
          </Button>
          <Button size="sm" variant="outline" className="h-8 border-slate-200" onClick={load} disabled={loading} data-testid="channels-overview-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
        </div>
      </div>
      {!data ? <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50">
              {cols.map(([c, cls]) => <th key={c} className={`px-4 py-2.5 font-medium ${cls || "text-right"}`}>{c}</th>)}
            </tr></thead>
            <tbody>
              {data.items.map((w) => (
                <tr key={w.id} className="border-t border-slate-100" data-testid={`overview-row-${w.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 min-w-[160px]">
                      <span className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: w.color + "22", color: w.color }}><Radio className="h-3.5 w-3.5" /></span>
                      <div className="min-w-0"><p className="font-medium text-slate-900 truncate">{w.name}</p><p className="text-[11px] text-slate-500 truncate">{w.phone_display || (w.wa_configured ? "collegato" : "non configurato")}</p></div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono2">{n(w.contacts)}</td>
                  <td className="px-4 py-3 text-right font-mono2">{n(w.campaigns)}</td>
                  <td className="px-4 py-3 text-right font-mono2" data-testid={`overview-sent-${w.id}`}>{n(w.sent)}</td>
                  <td className="px-4 py-3 text-right font-mono2">{n(w.delivered)} <span className="text-xs text-slate-500">({w.delivered_pct}%)</span></td>
                  <td className="px-4 py-3 text-right font-mono2">{n(w.sms_sent)}</td>
                  <td className="px-4 py-3 text-right font-mono2">{n(w.clicks)}</td>
                  <td className="px-4 py-3 text-right font-mono2">{eur(w.cost)} <span className="text-[10px] text-slate-400">{w.cost_source === "meta" ? "reale" : "stima"}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(100, w.quota_pct)}%`, backgroundColor: w.quota_pct > 90 ? "#EF4444" : w.color }} /></div>
                      <span className="text-xs font-mono2 text-slate-600 whitespace-nowrap">{n(w.quota_used)} / {n(w.quota_limit)}</span>
                    </div>
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold" data-testid="overview-totals">
                <td className="px-4 py-3 text-slate-900">Totale ({data.items.length} canali)</td>
                <td className="px-4 py-3 text-right font-mono2">{n(data.totals.contacts)}</td>
                <td className="px-4 py-3 text-right font-mono2">{n(data.totals.campaigns)}</td>
                <td className="px-4 py-3 text-right font-mono2">{n(data.totals.sent)}</td>
                <td className="px-4 py-3 text-right font-mono2">{n(data.totals.delivered)} <span className="text-xs text-slate-500 font-normal">({data.totals.delivered_pct}%)</span></td>
                <td className="px-4 py-3 text-right font-mono2">{n(data.totals.sms_sent)}</td>
                <td className="px-4 py-3 text-right font-mono2">{n(data.totals.clicks)}</td>
                <td className="px-4 py-3 text-right font-mono2">{eur(data.totals.cost)}</td>
                <td className="px-4 py-3 text-xs font-mono2 text-slate-600">{n(data.totals.quota_used)} / {n(data.totals.quota_limit)} ({data.totals.quota_pct}%)</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
